<template>
  <div class="flex-1">
    <div ref="player" class="reel-player aspect-16-9 relative" :class="{ '-fullscreen': isFullScreen }" v-if="project">
      <div class="flex flex-col absolute inset-0">
        <div class="reel-clip-player bg-black relative w-full flex-1" :class="{ '-fullscreen': isFullScreen }">
          <PublishedReelVideo :video="video" @click="togglePause" />
        </div>

        <BigPlayButton v-if="!hasEverPlayed" @click="resume"/>

        <ControlBar namespace="publishedReelPlayer" class="absolute bottom-0 left-0 right-0"/>
      </div>

      <div v-if="showFooter" class="reel-footer"/>
    </div>
  </div>
</template>


<script>
import ControlBar from './ControlBar'
import BigPlayButton from './BigPlayButton'
import PublishedReelVideo from './PublishedReelVideo'

import {mapGetters, mapState, mapActions} from 'vuex'
import fullScreenEmitter from '@/utils/fullScreenEmitter'
import requestFullscreen from '@/utils/requestFullscreen'
import exitFullscreen from '@/utils/exitFullscreen'
import isIos from '@/utils/isIos'

export default {
  components: {
    ControlBar,
    BigPlayButton,
    PublishedReelVideo,
  },
  props: {
    autoplay: {type: Boolean, default: false},
    video: {type: Object, required: true},
  },
  data() {
    return {
      keyPressListener: null,
      fullScreenEmitterListener: null,
      fullScreenChangeListener: null,
      showFooter: isIos(),
    }
  },
  computed: {
    ...mapState("publishedReelPlayer", [
      "project",
      'isPaused',
      'isFullScreen',
      'hasEverPlayed',
    ]),

    ...mapGetters("publishedReelPlayer", [
      'clipsInPlayOrder',
    ]),
  },
  watch: {
    isFullScreen(isFullScreen) {
      if (!isFullScreen && (document.fullscreenElement || document.fullscreen)) {
        exitFullscreen()
      }
    },
  },
  methods: {
    ...mapActions("publishedReelPlayer", [
      'pause',
      'resume',
      'setFullScreen',
    ]),
    togglePause() {
      this.isPaused ? this.resume() : this.pause()
    },
  },

  mounted() {
    document.addEventListener('keypress', this.keyPressListener = e => {
      if (e.key == ' ') {
        this.togglePause()
        e.preventDefault()
      }
    })

    document.addEventListener('fullscreenchange', this.fullScreenChangeListener = () => {
      if (!document.fullscreen) {
        this.setFullScreen(false)
      }
    })

    fullScreenEmitter.on(this.fullScreenEmitterListener = () => requestFullscreen(this.$refs.player))

    if (this.autoplay) {
      this.resume()
    }
  },

  beforeDestroy() {
    document.removeEventListener('keypress', this.keyPressListener)
    document.removeEventListener('fullscreenchange', this.fullScreenChangeListener)

    fullScreenEmitter.off(this.fullScreenEmitterListener)
  },
}
</script>

<style lang="scss" scoped>
.reel-player {
  flex-grow: 1;

  &.-fullscreen {
    display: flex;
    flex-direction: column;
    position: fixed;
    width: 100vw;
    height: 100vh;
    top: 0;
    left: 0;
    z-index: 100;
  }
}

.reel-footer {
  display: none;
  height: 114px;
  background-color: #212C35;
  background-image: url(https://sportsrecruits.com/images/sr_otg/sr_logo_red_white.svg);
  background-position: center;
  background-repeat: no-repeat;
}

.-fullscreen .reel-footer {
  @media (orientation: portrait) {
    display: block;
  }
}
</style>

<style lang="scss" scoped>
.-inactive {
  opacity: 0 !important;
  z-index: -100 !important;
}

.-loading .reel-clip-player-lining {
  opacity: 0 !important;
}

.reel-clip-player-outer {
  background: black;
}

.reel-clip-player-outer,
.reel-clip-player-lining,
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.loading-overlay {
  background: black;
}

.reel-clip-player.-loading .reel-clip-player-lining {
  display: none !important;
}

.video-spinner {
  position: absolute;
  top: calc(50% - 23px);
  left: calc(50% - 23px);
  transform: translate(-50%, -50%);
}

.reel-clip-player {
  .video-js {
    position: relative;
    width: 100% !important;
    height: 100% !important;

    cursor: pointer;

    display: flex;
    align-items: center;
    justify-content: center;
  }

  .vjs-loading-spinner {
    animation: hide-initially 1s 1;
  }

  .vjs-volume-panel,
  .vjs-progress-control,
  .vjs-time-control,
  .vjs-live-control,
  .vjs-seek-to-live-control,
  .vjs-fullscreen-control,
  .vjs-play-control,
  .vjs-control-bar,
  .vjs-big-play-button,
  .vjs-resize-manager
  {
    display: none !important;
  }

  .vjs-tech {
    position: relative;
    pointer-events: none !important;
  }
}

@keyframes hide-initially {
  0%, 99% { opacity: 0 }
  100% { opacity: 1 }
}
</style>